import { useState, useEffect, useCallback, useRef } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import moment from 'moment';

import { Button, Modal } from 'antd';
import Title from 'antd/lib/typography/Title';
import { WarningOutlined } from '@ant-design/icons';

import { logout } from 'store/auth/actions';

interface SessionTimeoutProps {
  logout(): any;
  loggedIn: boolean;
}

const SessionTimeout = (props: SessionTimeoutProps) => {
  const [events, setEvents] = useState(['click', 'load', 'scroll']);
  const [second, setSecond] = useState(0);
  const [isOpen, setOpen] = useState(false);
  const [maxTime, setMaxTime] = useState(15);

  let timeStamp;
  let warningInactiveInterval = useRef() as any;
  let startTimerInterval = useRef() as any;

  // start inactive check
  let timeChecker = () => {
    startTimerInterval.current = window.setTimeout(() => {
      let storedTimeStamp = sessionStorage.getItem('lastTimeStamp');
      warningInactive(storedTimeStamp);
    }, 60000);
  };

  // warning timer
  let warningInactive = (timeString: any) => {
    clearTimeout(startTimerInterval.current);

    warningInactiveInterval.current = setInterval(() => {
      const popTime = maxTime - 1;

      const diff = moment.duration(moment().diff(moment(timeString)));
      const minPast = diff.minutes();
      const leftSecond = 60 - diff.seconds();

      if (minPast === popTime) {
        setSecond(leftSecond);
        setOpen(true);
      }

      if (minPast === maxTime) {
        clearInterval(warningInactiveInterval.current);
        setOpen(false);
        sessionStorage.removeItem('lastTimeStamp');
        props.logout();
      }
    }, 1000);
  };

  // reset interval timer
  let resetTimer = useCallback(() => {
    clearTimeout(startTimerInterval.current);
    clearInterval(warningInactiveInterval.current);

    if (props.loggedIn) {
      timeStamp = moment();
      sessionStorage.setItem('lastTimeStamp', timeStamp.toString());
    } else {
      clearInterval(warningInactiveInterval.current);
      sessionStorage.removeItem('lastTimeStamp');
    }
    timeChecker();
    setOpen(false);
  }, [props.loggedIn]);

  // handle close popup
  const handleClose = () => {
    setOpen(false);
    resetTimer();
  };

  useEffect(() => {
    if (props.loggedIn && !sessionStorage.getItem('lastTimeStamp')) {
      sessionStorage.setItem('lastTimeStamp', moment().toString());
    }
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    timeChecker();

    return () => {
      clearTimeout(startTimerInterval.current);
    };
  }, [resetTimer, events, timeChecker]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={<><WarningOutlined /> Warning!</>}
      visible={isOpen}
      maskClosable={false}
      footer={[
        <Button
          key="cancel"
          type="primary"
          onClick={handleClose}
        >
          Close
        </Button>
      ]}
    >
      <Title level={5}>You will be logged out due to inactivity in {second} seconds.</Title>
      <p>Please click anywhere to continue your session.</p>
    </Modal>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  loggedIn: store.rootReducer.auth.loggedIn,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  logout,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SessionTimeout);